/**
 * Password Validation Utilities
 *
 * This module provides comprehensive password validation functionality
 * with security requirements for the BiteWise provider registration system.
 * Implements industry-standard password security practices.
 *
 * @module utils/validation/passwordValidation
 */

import type { ValidationResult } from '@/types/registration';

/**
 * Password validation configuration
 */
const PASSWORD_CONFIG = {
  /** Minimum password length */
  MIN_LENGTH: 8,
  /** Maximum password length */
  MAX_LENGTH: 128,
  /** Require uppercase letters */
  REQUIRE_UPPERCASE: true,
  /** Require lowercase letters */
  REQUIRE_LOWERCASE: true,
  /** Require numbers */
  REQUIRE_NUMBERS: true,
  /** Require special characters */
  REQUIRE_SPECIAL_CHARS: false, // Optional for better UX
  /** Maximum consecutive identical characters */
  MAX_CONSECUTIVE_CHARS: 3
} as const;

/**
 * Password validation error messages
 */
const PASSWORD_ERRORS = {
  REQUIRED: 'Password is required',
  TOO_SHORT: `Password must be at least ${PASSWORD_CONFIG.MIN_LENGTH} characters long`,
  TOO_LONG: `Password is too long (maximum ${PASSWORD_CONFIG.MAX_LENGTH} characters)`,
  NO_UPPERCASE: 'Password must contain at least one uppercase letter',
  NO_LOWERCASE: 'Password must contain at least one lowercase letter',
  NO_NUMBERS: 'Password must contain at least one number',
  NO_SPECIAL_CHARS: 'Password must contain at least one special character',
  TOO_MANY_CONSECUTIVE: `Password cannot contain more than ${PASSWORD_CONFIG.MAX_CONSECUTIVE_CHARS} consecutive identical characters`,
  COMMON_PASSWORD: 'This password is too common. Please choose a more secure password',
  CONTAINS_EMAIL: 'Password cannot contain your email address',
  CONTAINS_NAME: 'Password cannot contain your name'
} as const;

/**
 * Password strength levels
 */
export const PasswordStrength = {
  WEAK: 'weak',
  FAIR: 'fair',
  GOOD: 'good',
  STRONG: 'strong'
} as const;

export type PasswordStrength = typeof PasswordStrength[keyof typeof PasswordStrength];

/**
 * Password strength result
 */
export interface PasswordStrengthResult {
  /** Strength level */
  strength: PasswordStrength;
  /** Numeric score (0-100) */
  score: number;
  /** Feedback messages for improvement */
  feedback: string[];
  /** Whether password meets minimum requirements */
  meetsRequirements: boolean;
}

/**
 * Regular expressions for password validation
 */
const PASSWORD_PATTERNS = {
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBERS: /\d/,
  SPECIAL_CHARS: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
  CONSECUTIVE_CHARS: /(.)\1{3,}/
} as const;

/**
 * Common passwords to reject (subset for demonstration)
 * In production, this would be a larger list or use a service
 */
const COMMON_PASSWORDS = new Set([
  'password',
  'password123',
  '123456',
  '12345678',
  'qwerty',
  'abc123',
  'password1',
  'admin',
  'letmein',
  'welcome',
  'monkey',
  '1234567890',
  'password!',
  'Password1',
  'iloveyou'
]);

/**
 * Validates password according to security requirements
 *
 * @param password - The password to validate
 * @param context - Additional context for validation (email, name)
 * @returns ValidationResult with isValid flag and error message if invalid
 *
 * @example
 * ```typescript
 * const result = validatePassword('MySecure123', {
 *   email: 'user@example.com',
 *   firstName: 'John',
 *   lastName: 'Doe'
 * });
 *
 * if (result.isValid) {
 *   logger.log('Password is valid');
 * } else {
 *   logger.log('Error:', result.error);
 * }
 * ```
 */
export const validatePassword = (
  password: string,
  context?: {
    email?: string;
    firstName?: string;
    lastName?: string;
  }
): ValidationResult => {
  // Check if password is provided
  if (!password || password.length === 0) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.REQUIRED
    };
  }

  // Check length constraints
  if (password.length < PASSWORD_CONFIG.MIN_LENGTH) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.TOO_SHORT
    };
  }

  if (password.length > PASSWORD_CONFIG.MAX_LENGTH) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.TOO_LONG
    };
  }

  // Check character requirements
  if (PASSWORD_CONFIG.REQUIRE_UPPERCASE && !PASSWORD_PATTERNS.UPPERCASE.test(password)) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.NO_UPPERCASE
    };
  }

  if (PASSWORD_CONFIG.REQUIRE_LOWERCASE && !PASSWORD_PATTERNS.LOWERCASE.test(password)) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.NO_LOWERCASE
    };
  }

  if (PASSWORD_CONFIG.REQUIRE_NUMBERS && !PASSWORD_PATTERNS.NUMBERS.test(password)) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.NO_NUMBERS
    };
  }

  if (PASSWORD_CONFIG.REQUIRE_SPECIAL_CHARS && !PASSWORD_PATTERNS.SPECIAL_CHARS.test(password)) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.NO_SPECIAL_CHARS
    };
  }

  // Check for too many consecutive characters
  if (PASSWORD_PATTERNS.CONSECUTIVE_CHARS.test(password)) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.TOO_MANY_CONSECUTIVE
    };
  }

  // Check against common passwords
  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    return {
      isValid: false,
      error: PASSWORD_ERRORS.COMMON_PASSWORD
    };
  }

  // Check context-specific rules
  if (context) {
    const contextValidation = validatePasswordContext(password, context);
    if (!contextValidation.isValid) {
      return contextValidation;
    }
  }

  return { isValid: true };
};

/**
 * Validates password against user context (email, name)
 *
 * @param password - The password to validate
 * @param context - User context information
 * @returns ValidationResult
 */
const validatePasswordContext = (
  password: string,
  context: {
    email?: string;
    firstName?: string;
    lastName?: string;
  }
): ValidationResult => {
  const lowerPassword = password.toLowerCase();

  // Check if password contains email
  if (context.email) {
    const emailLocal = context.email.split('@')[0]?.toLowerCase();
    if (emailLocal && emailLocal.length > 3 && lowerPassword.includes(emailLocal)) {
      return {
        isValid: false,
        error: PASSWORD_ERRORS.CONTAINS_EMAIL
      };
    }
  }

  // Check if password contains name
  if (context.firstName && context.firstName.length > 2) {
    if (lowerPassword.includes(context.firstName.toLowerCase())) {
      return {
        isValid: false,
        error: PASSWORD_ERRORS.CONTAINS_NAME
      };
    }
  }

  if (context.lastName && context.lastName.length > 2) {
    if (lowerPassword.includes(context.lastName.toLowerCase())) {
      return {
        isValid: false,
        error: PASSWORD_ERRORS.CONTAINS_NAME
      };
    }
  }

  return { isValid: true };
};

/**
 * Calculates password strength and provides feedback
 *
 * @param password - The password to analyze
 * @returns PasswordStrengthResult with strength level and feedback
 *
 * @example
 * ```typescript
 * const strength = calculatePasswordStrength('MySecure123!');
 * logger.log(`Strength: ${strength.strength}, Score: ${strength.score}`);
 * logger.log('Feedback:', strength.feedback);
 * ```
 */
export const calculatePasswordStrength = (password: string): PasswordStrengthResult => {
  let score = 0;
  const feedback: string[] = [];

  // Length scoring
  if (password.length >= 8) score += 25;
  else feedback.push('Use at least 8 characters');

  if (password.length >= 12) score += 15;
  else if (password.length >= 8) feedback.push('Consider using 12+ characters for better security');

  // Character variety scoring
  if (PASSWORD_PATTERNS.LOWERCASE.test(password)) {
    score += 15;
  } else {
    feedback.push('Add lowercase letters');
  }

  if (PASSWORD_PATTERNS.UPPERCASE.test(password)) {
    score += 15;
  } else {
    feedback.push('Add uppercase letters');
  }

  if (PASSWORD_PATTERNS.NUMBERS.test(password)) {
    score += 15;
  } else {
    feedback.push('Add numbers');
  }

  if (PASSWORD_PATTERNS.SPECIAL_CHARS.test(password)) {
    score += 15;
  } else {
    feedback.push('Consider adding special characters (!@#$%^&*)');
  }

  // Complexity bonus
  const uniqueChars = new Set(password).size;
  if (password.length > 1) {
    if (uniqueChars >= password.length * 0.7) {
      score += 10;
    } else {
      feedback.push('Avoid repeating characters');
    }
  }


  // Common password penalty
  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    score = Math.max(0, score - 30);
    feedback.push('Avoid common passwords');
  }

  // Determine strength level
  let strength: PasswordStrength;
  if (score >= 80) strength = PasswordStrength.STRONG;
  else if (score >= 60) strength = PasswordStrength.GOOD;
  else if (score >= 40) strength = PasswordStrength.FAIR;
  else strength = PasswordStrength.WEAK;

  // Check if meets minimum requirements
  const meetsRequirements = validatePassword(password).isValid;

  return {
    strength,
    score: Math.min(100, score),
    feedback,
    meetsRequirements
  };
};

/**
 * Validates password confirmation matches original password
 *
 * @param password - Original password
 * @param confirmPassword - Confirmation password
 * @returns ValidationResult
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword || confirmPassword.length === 0) {
    return {
      isValid: false,
      error: 'Please confirm your password'
    };
  }

  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: 'Passwords do not match'
    };
  }

  return { isValid: true };
};

/**
 * Generates password strength indicator color
 *
 * @param strength - Password strength level
 * @returns CSS color class or hex color
 */
export const getPasswordStrengthColor = (strength: PasswordStrength): string => {
  switch (strength) {
    case PasswordStrength.WEAK:
      return 'text-red-600';
    case PasswordStrength.FAIR:
      return 'text-orange-600';
    case PasswordStrength.GOOD:
      return 'text-blue-600';
    case PasswordStrength.STRONG:
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Gets user-friendly password strength label
 *
 * @param strength - Password strength level
 * @returns Human-readable strength label
 */
export const getPasswordStrengthLabel = (strength: PasswordStrength): string => {
  switch (strength) {
    case PasswordStrength.WEAK:
      return 'Weak';
    case PasswordStrength.FAIR:
      return 'Fair';
    case PasswordStrength.GOOD:
      return 'Good';
    case PasswordStrength.STRONG:
      return 'Strong';
    default:
      return 'Unknown';
  }
};